import * as THREE from "three";
import { CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer.js";
import { ENVELOPE_BASE_HALF, ENVELOPE_KNOB_LABEL_DY, envelopeKnobLayout, knobOutput, KNOB_VALUE_LABEL_Z, NODE_TITLE_LABEL_DY, PlacedObject, pitchQuantizeMask } from "../objects";
import { maskToClasses } from "../pitchScale";
import { timbreName } from "../audio";
import { ValueNode } from "../signals";
import { AppContext } from "../appContext";
import { OPERATOR_LABELS } from "./controls";
import { t } from "../i18n";

interface NodeLabelsDeps {
  buildValueNodes(): Map<string, ValueNode>;
  uiView(): boolean;
}

export interface NodeLabels {
  updateNodeLabels(): void;
}

interface NodeLabel {
  object: CSS2DObject;
  text: HTMLDivElement;
  bar: HTMLDivElement;
  fill: HTMLDivElement;
}

/**
 * CSS2D value/timer/decay/timbre/delay/router node labels and the named-group
 * label, re-parented to each object's current mesh every frame. The group label
 * shows only while that group is selected; the group's own selection frame
 * (OBB LineSegments on its pick box) carries the border, so no separate wireframe is
 * drawn.
 */
export function initNodeLabels(ctx: AppContext, deps: NodeLabelsDeps): NodeLabels {
  const { field, graph, evaluator } = ctx;

  const nodeLabelMap = new Map<PlacedObject, NodeLabel>();
  const groupLabelMap = new Map<PlacedObject, CSS2DObject>();
  const titleLabelMap = new Map<PlacedObject, CSS2DObject>();
  const envLabelMap = new Map<PlacedObject, CSS2DObject[]>();
  const knobValueMap = new Map<PlacedObject, Map<string, CSS2DObject>>();

  function knobParamValue(obj: PlacedObject, param: string): number {
    if (obj.type !== "envelope") {
      return knobOutput(obj);
    }
    if (param === "attack") {
      return obj.params.x;
    }
    if (param === "decay") {
      return obj.params.y;
    }
    if (param === "sustain") {
      return obj.params.z;
    }
    return obj.params.w;
  }

  function formatDisplayValue(v: number): string {
    return Number.isInteger(v) ? String(v) : v.toFixed(2);
  }

  /**
   * Labels and group wireframes are re-parented to the object's current mesh
   * every frame, not only at creation: an edit can rebuild the mesh
   * (type / params / arc patches), and a CSS2D label left on the removed mesh
   * is no longer traversed by the renderer — its DOM element freezes at the
   * last rendered screen position.
   */
  function updateNodeLabels(): void {
    const current = field.list();
    const currentSet = new Set<PlacedObject>(current);
    const showValueLabels = true;

    const valuesToEvict: PlacedObject[] = [];
    for (const [obj] of nodeLabelMap) {
      if (!currentSet.has(obj) || (obj.type !== "calc" && obj.type !== "count" && obj.type !== "timer" && obj.type !== "decay" && obj.type !== "timbre" && obj.type !== "pitchQuantize" && obj.type !== "delay" && obj.type !== "router" && obj.type !== "global") || !showValueLabels || (deps.uiView() && (obj.type === "timbre" || obj.type === "pitchQuantize" || obj.type === "global"))) {
        valuesToEvict.push(obj);
      }
    }
    for (const obj of valuesToEvict) {
      nodeLabelMap.get(obj)!.object.removeFromParent();
      nodeLabelMap.delete(obj);
    }

    const groupLabelsToEvict: PlacedObject[] = [];
    for (const [obj] of groupLabelMap) {
      if (!currentSet.has(obj) || obj.type !== "group" || !obj.name || deps.uiView() || field.selectedObject() !== obj) {
        groupLabelsToEvict.push(obj);
      }
    }
    for (const obj of groupLabelsToEvict) {
      groupLabelMap.get(obj)!.removeFromParent();
      groupLabelMap.delete(obj);
    }

    const titleLabelsToEvict: PlacedObject[] = [];
    for (const [obj] of titleLabelMap) {
      if (!currentSet.has(obj) || (obj.type !== "button" && obj.type !== "knob" && obj.type !== "display") || !obj.name || deps.uiView()) {
        titleLabelsToEvict.push(obj);
      }
    }
    for (const obj of titleLabelsToEvict) {
      titleLabelMap.get(obj)!.removeFromParent();
      titleLabelMap.delete(obj);
    }

    const envLabelsToEvict: PlacedObject[] = [];
    for (const [obj] of envLabelMap) {
      if (!currentSet.has(obj) || obj.type !== "envelope" || !showValueLabels) {
        envLabelsToEvict.push(obj);
      }
    }
    for (const obj of envLabelsToEvict) {
      for (const label of envLabelMap.get(obj)!) {
        label.removeFromParent();
      }
      envLabelMap.delete(obj);
    }

    const knobValuesToEvict: PlacedObject[] = [];
    for (const [obj] of knobValueMap) {
      const keep = currentSet.has(obj) && (obj.type === "knob" || (obj.type === "envelope" && showValueLabels));
      if (!keep) {
        knobValuesToEvict.push(obj);
      }
    }
    for (const obj of knobValuesToEvict) {
      for (const label of knobValueMap.get(obj)!.values()) {
        label.removeFromParent();
      }
      knobValueMap.delete(obj);
    }

    if (showValueLabels) {
      const nodes = deps.buildValueNodes();
      for (let i = 0; i < current.length; i++) {
        const obj = current[i];
        if (obj.type !== "calc" && obj.type !== "count" && obj.type !== "timer" && obj.type !== "decay" && obj.type !== "timbre" && obj.type !== "pitchQuantize" && obj.type !== "delay" && obj.type !== "router" && obj.type !== "global") {
          continue;
        }
        if (deps.uiView() && (obj.type === "timbre" || obj.type === "pitchQuantize" || obj.type === "global")) {
          continue;
        }
        let label = nodeLabelMap.get(obj);
        if (!label) {
          const div = document.createElement("div");
          div.className = "node-label";
          const text = document.createElement("div");
          const bar = document.createElement("div");
          bar.className = "node-label-bar";
          const fill = document.createElement("div");
          fill.className = "node-label-bar-fill";
          bar.appendChild(fill);
          div.append(text, bar);
          label = { object: new CSS2DObject(div), text, bar, fill };
          nodeLabelMap.set(obj, label);
        }
        const mesh = field.meshAt(i);
        if (mesh && label.object.parent !== mesh) {
          mesh.add(label.object);
        }
        if (obj.type === "timbre") {
          const set = obj.timbreIds;
          const detail = set.length === 0
            ? "—"
            : set.length === 1
              ? timbreName(set[0])
              : `${timbreName(set[0])} +${set.length - 1}`;
          label.text.textContent = `${t("nodeLabel.timbre")}\n${detail}`;
          label.bar.style.display = "none";
          continue;
        }
        if (obj.type === "pitchQuantize") {
          const onCount = maskToClasses(pitchQuantizeMask(obj)).length;
          label.text.textContent = `${t("nodeLabel.pitchQuantize")}\n${onCount}/12`;
          label.bar.style.display = "none";
          continue;
        }
        if (obj.type === "delay") {
          label.text.textContent = `${t("nodeLabel.delay")}\n${obj.delayS.toFixed(2)}s`;
          label.bar.style.display = "none";
          continue;
        }
        if (obj.type === "router") {
          const next = evaluator.routerNextOut(graph, obj.id);
          label.text.textContent = next > 0 ? `${t("nodeLabel.router")}\n\u2192${next}` : `${t("nodeLabel.router")}\n\u2014`;
          label.bar.style.display = "none";
          continue;
        }
        if (obj.type === "global") {
          label.text.textContent = t("nodeLabel.global");
          label.bar.style.display = "none";
          continue;
        }
        const heading = obj.type === "timer"
          ? (obj.timerMode === "time" ? t("timer.modeTime") : t("timer.modeLoop"))
          : obj.type === "decay" ? t("nodeLabel.decay")
          : obj.type === "count" ? t("nodeLabel.count")
          : OPERATOR_LABELS[obj.operator];
        const v = obj.type === "count" ? (field.countReadoutAt(i) ?? evaluator.valueOut(graph, obj.id, nodes)) : evaluator.valueOut(graph, obj.id, nodes);
        label.text.textContent = `${heading}\n${formatDisplayValue(v)}`;
        label.fill.style.width = `${Math.min(1, Math.max(0, v)) * 100}%`;
      }

      for (let i = 0; i < current.length; i++) {
        const obj = current[i];
        if (obj.type !== "envelope") {
          continue;
        }
        let labels = envLabelMap.get(obj);
        if (!labels) {
          const layout = envelopeKnobLayout();
          labels = layout.map((k: { param: string; x: number; y: number }): CSS2DObject => {
            const div = document.createElement("div");
            div.className = "node-label";
            div.textContent = k.param.charAt(0).toUpperCase();
            const o = new CSS2DObject(div);
            o.position.set(k.x, k.y + ENVELOPE_KNOB_LABEL_DY, ENVELOPE_BASE_HALF.z);
            return o;
          });
          envLabelMap.set(obj, labels);
        }
        const mesh = field.meshAt(i);
        if (mesh) {
          for (const label of labels) {
            if (label.parent !== mesh) {
              mesh.add(label);
            }
          }
        }
      }
    }

    for (let i = 0; i < current.length; i++) {
      const obj = current[i];
      if (obj.type !== "group") {
        continue;
      }
      if (obj.name && !deps.uiView() && field.selectedObject() === obj) {
        let label = groupLabelMap.get(obj);
        if (!label) {
          const div = document.createElement("div");
          div.className = "node-label group-label";
          label = new CSS2DObject(div);
          label.position.set(0, 0.5, 0);
          label.center.set(0.5, 1);
          groupLabelMap.set(obj, label);
        }
        label.element.textContent = obj.name;
        const mesh = field.meshAt(i);
        if (mesh && label.parent !== mesh) {
          mesh.add(label);
        }
      }
    }

    for (let i = 0; i < current.length; i++) {
      const obj = current[i];
      if ((obj.type !== "button" && obj.type !== "knob" && obj.type !== "display") || !obj.name || deps.uiView()) {
        continue;
      }
      let label = titleLabelMap.get(obj);
      if (!label) {
        const div = document.createElement("div");
        div.className = "node-label node-title-label";
        label = new CSS2DObject(div);
        label.position.set(0, NODE_TITLE_LABEL_DY, 0);
        label.center.set(0.5, 1);
        titleLabelMap.set(obj, label);
      }
      label.element.textContent = obj.name;
      const mesh = field.meshAt(i);
      if (mesh && label.parent !== mesh) {
        mesh.add(label);
      }
    }

    for (let i = 0; i < current.length; i++) {
      const obj = current[i];
      if (obj.type !== "knob" && !(obj.type === "envelope" && showValueLabels)) {
        continue;
      }
      const mesh = field.meshAt(i);
      const controls = mesh?.userData.knobControls as Record<string, THREE.Object3D> | undefined;
      if (!controls) {
        continue;
      }
      let labels = knobValueMap.get(obj);
      if (!labels) {
        labels = new Map<string, CSS2DObject>();
        knobValueMap.set(obj, labels);
      }
      for (const param of Object.keys(controls)) {
        const cap = controls[param];
        let label = labels.get(param);
        if (!label) {
          const div = document.createElement("div");
          div.className = "node-label";
          label = new CSS2DObject(div);
          label.position.set(0, 0, KNOB_VALUE_LABEL_Z);
          labels.set(param, label);
        }
        if (label.parent !== cap) {
          cap.add(label);
        }
        const paramValue = knobParamValue(obj, param);
        label.element.textContent = obj.type === "knob" ? formatDisplayValue(paramValue) : paramValue.toFixed(2);
      }
    }
  }

  return { updateNodeLabels };
}
